"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.setMemberSkillRequestSchema = exports.memberSkillSchema = exports.updateSkillRequestSchema = exports.createSkillRequestSchema = exports.skillSchema = exports.updateSkillLevelRequestSchema = exports.createSkillLevelRequestSchema = exports.skillLevelSchema = exports.updateSkillCategoryRequestSchema = exports.createSkillCategoryRequestSchema = exports.skillCategorySchema = void 0;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Skills — C54, Phase 5.                                                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A VOCABULARY, NOT AN AUTHORIZATION SOURCE.** Holding a skill grants nothing, routes
 * nothing and gates nothing — the same rule C31 set for project roles and C45 set for `mgr`.
 * Phase 5 draws no screen that matches people to work by skill, so nothing reads these shapes to
 * decide anything, and a matcher would be a decision rather than a query somebody adds.
 *
 * ⚠️ **Three lists and a join, because the mockup keeps three lists.** Categories give skills their
 * colour, levels give them a scale, and a member holds one level in each skill they have.
 */
/* ── categories ──────────────────────────────────────────────────────────────────────────── */
exports.skillCategorySchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `skill_category_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('skillCategory'),
    name: zod_1.z.string(),
    /** ⚠️ The category is where a skill's colour comes from — a skill carries none of its own. */
    color: zod_1.z.string(),
    sortOrder: zod_1.z.number().int(),
    /**
     * ⚠️ How many skills sit in it. The settings row says it **before** the delete is offered, which
     * is the same job `claimCount` does for an overtime type: a refusal is never a surprise.
     */
    skillCount: zod_1.z.number().int().nonnegative(),
    version: zod_1.z.number().int(),
});
exports.createSkillCategoryRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(120),
    color: zod_1.z.string().regex(/^#[0-9A-Fa-f]{6}$/),
});
exports.updateSkillCategoryRequestSchema = exports.createSkillCategoryRequestSchema.extend({
    version: zod_1.z.number().int().nonnegative(),
});
/* ── levels ──────────────────────────────────────────────────────────────────────────────── */
/**
 * ┌──────────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE ORDER IS THE WEIGHT, AND `rank` IS COMPUTED FROM IT.                │
 * └──────────────────────────────────────────────────────────────────────────────┘
 *
 * The mockup states it in its own comment — *"a szintek sorrendje adja a súlyt: az első a
 * leggyengébb"* — and its `skLevelW` is literally `indexOf + 1`. So dragging *Mentor* above
 * *Kezdő* does not re-label anything; it inverts the scale, and every person's skill list re-sorts
 * underneath it.
 *
 * ⚠️ **`rank` is therefore derived and never stored.** Two columns holding one fact, one of which
 * is edited by dragging, is a pair that goes out of step — `0149` says the same thing from the
 * schema's side.
 */
exports.skillLevelSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `skill_level_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('skillLevel'),
    name: zod_1.z.string(),
    color: zod_1.z.string(),
    /**
     * ⚠️ **What makes a level mean the same thing to two people.** *"Ismeri az alapokat, de
     * segítséggel dolgozik benne"* is the difference between a scale and five words. Optional,
     * because the mockup's levels mostly have one and its skills mostly do not.
     */
    description: zod_1.z.string().nullable(),
    sortOrder: zod_1.z.number().int(),
    /** ⚠️ Derived from the position: 1 is the weakest. Never stored — see above. */
    rank: zod_1.z.number().int().positive(),
    /** How many member-skill rows sit at this level. The delete guard's figure. */
    holderCount: zod_1.z.number().int().nonnegative(),
    version: zod_1.z.number().int(),
});
exports.createSkillLevelRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(120),
    color: zod_1.z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    description: zod_1.z.string().trim().max(500).nullable().optional(),
});
exports.updateSkillLevelRequestSchema = exports.createSkillLevelRequestSchema.extend({
    version: zod_1.z.number().int().nonnegative(),
});
/* ── the skills themselves ───────────────────────────────────────────────────────────────── */
exports.skillSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `skill_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('skill'),
    name: zod_1.z.string(),
    categoryId: zod_1.z.string(),
    /** ⚠️ Denormalised for the list, which draws the category's name and dot on every row. */
    categoryName: zod_1.z.string(),
    categoryColor: zod_1.z.string(),
    description: zod_1.z.string().nullable(),
    sortOrder: zod_1.z.number().int(),
    /** How many people hold it. The delete guard's figure. */
    holderCount: zod_1.z.number().int().nonnegative(),
    version: zod_1.z.number().int(),
});
exports.createSkillRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(120),
    /** ⚠️ Required — the mockup refuses to save a skill with no category. */
    categoryId: zod_1.z.string().min(1),
    description: zod_1.z.string().trim().max(500).nullable().optional(),
});
exports.updateSkillRequestSchema = exports.createSkillRequestSchema.extend({
    version: zod_1.z.number().int().nonnegative(),
});
/* ── who holds what ──────────────────────────────────────────────────────────────────────── */
/**
 * One skill a person holds, at one level.
 *
 * ⚠️ **Everything a row draws travels with it**, because the four surfaces that render these —
 * the account tab, the person screen, the onboarding step and the picker — otherwise each need
 * the whole vocabulary loaded to print one line.
 */
exports.memberSkillSchema = zod_1.z.object({
    id: zod_1.z.string(),
    skillId: zod_1.z.string(),
    skillName: zod_1.z.string(),
    categoryName: zod_1.z.string(),
    categoryColor: zod_1.z.string(),
    levelId: zod_1.z.string(),
    levelName: zod_1.z.string(),
    levelColor: zod_1.z.string(),
    levelDescription: zod_1.z.string().nullable(),
    /** ⚠️ The level's position, which is what the list sorts on. See `skillLevelSchema`. */
    levelRank: zod_1.z.number().int().positive(),
    version: zod_1.z.number().int(),
});
/**
 * ⚠️⚠️ **ONE SHAPE FOR ADD AND FOR CHANGE-THE-LEVEL, BECAUSE THE DATABASE HAS ONE ROW EITHER
 * WAY.** `uSkillSet` in the mockup either replaces the pair or appends it, and
 * `member_skills_one_per_skill` makes the second call on the same skill an update rather than a
 * duplicate. Two endpoints would be two ways to reach one row.
 */
exports.setMemberSkillRequestSchema = zod_1.z.object({
    skillId: zod_1.z.string().min(1),
    levelId: zod_1.z.string().min(1),
});
/*
 * ⚠️ **THERE IS NO `reorderSkillsRequestSchema`, DELIBERATELY.** All three lists reorder with
 * the shared `reorderRequestSchema` from `tasks/contract` — a permutation of the whole set, never
 * a move. A fourth copy of `{ ids: string[] }` would be a fourth thing to keep in step for no
 * gain, and `metafield.reorder` already settled both the shape and the argument for it.
 */
//# sourceMappingURL=skills.js.map